import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle2, Mail, Download, ArrowRight, Loader2 } from "lucide-react";
import { FOUNDATION_INFO } from "@/lib/initiatives";

export default function DonationSuccess() {
  const [params] = useSearchParams();
  const id = params.get("id");
  const [donation, setDonation] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!id) return;
    api
      .get(`/donations/${id}`)
      .then((r) => setDonation(r.data))
      .catch(() => setErr("We couldn't load your donation details."));
  }, [id]);

  if (!id)
    return (
      <div className="section-py text-center">
        <p>Missing donation reference.</p>
      </div>
    );

  return (
    <section className="section-py">
      <div className="mx-auto max-w-3xl container-x">
        <div className="flex flex-col items-center text-center">
          <div className="w-16 h-16 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
            <CheckCircle2 className="w-8 h-8 text-[#0A2540]" strokeWidth={1.5} />
          </div>
          <h1 className="mt-6 font-serif-display text-4xl md:text-5xl text-[#0A2540]">
            Thank you for your kindness.
          </h1>
          <p className="mt-4 text-[#4A5568] max-w-xl">
            Your contribution is recorded. An official 80G receipt has been sent to your
            email address.
          </p>
        </div>

        {!donation && !err && (
          <div className="mt-10 flex items-center justify-center gap-2 text-[#4A5568]">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading receipt…
          </div>
        )}
        {err && <p className="mt-10 text-center text-red-600">{err}</p>}

        {donation && (
          <div className="mt-12 rounded-3xl border border-[#0A2540]/10 bg-white p-8 md:p-10" data-testid="donation-receipt">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <div className="text-xs uppercase tracking-[0.24em] text-[#4A5568]">Official Receipt</div>
                <div className="mt-1 font-serif-display text-2xl text-[#0A2540]">
                  {donation.receipt_no}
                </div>
              </div>
              <div className="text-right">
                <div className="text-xs uppercase tracking-[0.16em] text-[#4A5568]">Amount</div>
                <div className="mt-1 font-serif-display text-3xl text-[#D4AF37]">
                  ₹{Number(donation.amount).toLocaleString("en-IN")}
                </div>
              </div>
            </div>

            <div className="mt-8 grid sm:grid-cols-2 gap-6 text-sm">
              <div>
                <div className="text-[#4A5568] text-xs uppercase tracking-[0.16em]">Donor</div>
                <div className="mt-1 font-medium text-[#0A2540]">{donation.donor_name}</div>
                <div className="text-[#4A5568]">{donation.donor_email}</div>
              </div>
              <div>
                <div className="text-[#4A5568] text-xs uppercase tracking-[0.16em]">Cause</div>
                <div className="mt-1 font-medium text-[#0A2540]">{donation.cause || "General"}</div>
              </div>
              <div>
                <div className="text-[#4A5568] text-xs uppercase tracking-[0.16em]">Payment Ref</div>
                <div className="mt-1 font-medium text-[#0A2540] break-all">
                  {donation.razorpay_payment_id}
                </div>
              </div>
              <div>
                <div className="text-[#4A5568] text-xs uppercase tracking-[0.16em]">Date</div>
                <div className="mt-1 font-medium text-[#0A2540]">
                  {new Date(donation.completed_at).toLocaleString("en-IN")}
                </div>
              </div>
            </div>

            <div className="mt-8 rounded-2xl bg-[#F9F8F6] border border-[#0A2540]/10 p-5 text-sm">
              <div className="text-xs uppercase tracking-[0.16em] text-[#4A5568] mb-2">
                80G Tax Benefit
              </div>
              <div className="grid sm:grid-cols-2 gap-2 text-[#0A2540]">
                <div>Approval No.: <span className="font-medium">{FOUNDATION_INFO.reg80g}</span></div>
                <div>PAN: <span className="font-medium">{FOUNDATION_INFO.pan}</span></div>
                <div>Approval Date: <span className="font-medium">{FOUNDATION_INFO.reg80g_date}</span></div>
                <div>Valid: <span className="font-medium">{FOUNDATION_INFO.ay_validity}</span></div>
              </div>
              <p className="mt-3 text-xs text-[#4A5568]">
                You may claim deduction under Section 80G of the Income Tax Act, 1961 based on this receipt.
              </p>
            </div>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                onClick={() => window.print()}
                data-testid="print-receipt-btn"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-[#0A2540] text-[#0A2540] px-6 py-3 font-medium hover:bg-[#0A2540] hover:text-white transition-colors"
              >
                <Download className="w-4 h-4" /> Print / save receipt
              </button>
              <Link
                to="/"
                data-testid="back-home-btn"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[#0A2540] text-white px-6 py-3 font-medium hover:bg-[#D4AF37] hover:text-[#0A2540] transition-colors"
              >
                Back to homepage <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <p className="mt-6 text-xs text-[#4A5568] flex items-center gap-2">
              <Mail className="w-3.5 h-3.5" /> Receipt emailed to {donation.donor_email}
            </p>
          </div>
        )}
      </div>
    </section>
  );
}
